/**
 * Uniquification sources: the overlay clips and background images mixed into each phone's variant.
 *
 * Two lists, one number. The number is **capacity** — how many phones one uniquified distribution can
 * serve — and it is what a person actually needs from this page: the Distribute dialog refuses a
 * distribution that needs more distinct recipes than the catalogue allows, so the question "do we have
 * enough" is answered here, before the click, not by the refusal.
 *
 * Upload reuses the video path (`uploadSource` in `api/upload.ts`): the same chunked, resumable
 * transfer, announced on `/v1/sources` with the kind.
 */

import { Trash2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { ApiError } from '../../api/client'
import type { Source, SourceKind } from '../../api/types'
import { uploadSource, type UploadProgress } from '../../api/upload'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { Badge, Card, Empty, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { megabytes } from '../videos/VideosPanel'

export const ACCEPT: Record<SourceKind, string> = {
  overlay: 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm',
  background: 'image/jpeg,image/png,.jpg,.jpeg,.png',
  music: 'audio/mpeg,audio/mp4,audio/wav,audio/aac,audio/ogg,.mp3,.m4a,.wav,.aac,.ogg',
  card: 'image/jpeg,image/png,.jpg,.jpeg,.png',
  banner: 'video/webm,video/quicktime,image/gif,.webm,.mov,.gif',
}

const GROUPS: { title: 'groupVariant' | 'groupPost'; kinds: SourceKind[] }[] = [
  { title: 'groupVariant', kinds: ['overlay', 'background', 'music'] },
  { title: 'groupPost', kinds: ['card', 'banner'] },
]

interface UploadJob {
  id: number
  name: string
  progress: UploadProgress
  error?: string
}

export function SourcesPanel() {
  const { api, authEpoch } = useApp()
  const t = useT()
  const list = usePoll(() => api.sources(), 5000, [api, authEpoch])
  const [toDelete, setToDelete] = useState<Source | null>(null)

  const remove = async () => {
    if (!toDelete) return
    try {
      await api.deleteSource(toDelete.source_id)
      toast(t.sources.deleted(toDelete.filename))
      setToDelete(null)
      list.refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const data = list.data
  return (
    <>
      <Card
        title={t.sources.title}
        sub={t.sources.sub}
        actions={data ? <Badge kind={data.capacity > 0 ? 'ok' : 'warn'}>{t.sources.capacity(data.capacity)}</Badge> : null}
      >
        <p className="dim small">{t.sources.rule}</p>
        <Status text={list.error ? t.sources.readError(list.error.message) : null} bad />
      </Card>
      {/* **In the order they work, in two groups** (owner, 2026-10-06: "картки … розкидані, треба логічно
          якось розмістити"). Every kind is a full-width card, so nothing waits for a taller neighbour:
          first what makes each phone's variant (overlay + background = the pair, then the music under
          it), then what dresses a post. Inside a card the files flow in columns. */}
      {GROUPS.map((g) => (
        <section className="source-group" key={g.title}>
          <h3 className="source-group-title">{t.sources[g.title]}</h3>
          {g.kinds.map((kind) => (
            <KindList
              key={kind}
              kind={kind}
              items={(data?.sources ?? []).filter((s) => s.kind === kind)}
              loaded={Boolean(data)}
              onChanged={() => list.refresh()}
              onDelete={setToDelete}
            />
          ))}
        </section>
      ))}
      {toDelete && (
        <ConfirmDanger
          title={t.sources.deleteTitle(toDelete.filename)}
          confirmLabel={t.sources.delete}
          holdSeconds={1}
          onCancel={() => setToDelete(null)}
          onConfirm={() => void remove()}
        >
          <p className="dim small">{t.sources.deleteBody}</p>
        </ConfirmDanger>
      )}
    </>
  )
}

function KindList({
  kind,
  items,
  loaded,
  onChanged,
  onDelete,
}: {
  kind: SourceKind
  items: Source[]
  loaded: boolean
  onChanged: () => void
  onDelete: (s: Source) => void
}) {
  const { api, auth } = useApp()
  const t = useT()
  const [uploads, setUploads] = useState<UploadJob[]>([])
  const fileInput = useRef<HTMLInputElement>(null)
  const nextId = useRef(1)

  const accept = async (files: FileList | null) => {
    if (!files) return
    for (const file of Array.from(files)) {
      const id = nextId.current++
      const patch = (p: Partial<UploadJob>) => setUploads((u) => u.map((j) => (j.id === id ? { ...j, ...p } : j)))
      setUploads((u) => [...u, { id, name: file.name, progress: { stage: 'hashing', done: 0, total: file.size } }])
      try {
        const source = await uploadSource(api, kind, file, (progress) => patch({ progress }))
        if (source.status === 'error') patch({ error: source.error ?? t.videos.rejected })
        else {
          toast(t.sources.uploaded(file.name))
          setUploads((u) => u.filter((j) => j.id !== id))
        }
        onChanged()
      } catch (e) {
        patch({ error: e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e) })
      }
    }
  }

  return (
    <Card
      className="source-kind"
      title={<>{t.sources.kind[kind]}{items.length > 0 && <span className="pill">{t.sources.files(items.length)}</span>}</>}
      sub={t.sources.kindHint[kind]}
      actions={
        <>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT[kind]}
            multiple
            hidden
            onChange={(e) => {
              void accept(e.target.files)
              e.target.value = ''
            }}
          />
          <button className="quiet tiny" onClick={() => fileInput.current?.click()}>
            <Upload className="ico" size={14} aria-hidden /> {t.sources.upload}
          </button>
        </>
      }
    >
      {uploads.length > 0 && (
        <div className="uploads">
          {uploads.map((u) => (
            <div className="upload-row" key={u.id}>
              <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.name}
              </span>
              {u.error ? (
                <>
                  <Badge kind="bad">{u.error}</Badge>
                  <button className="ghost" aria-label={t.common.dismiss} title={t.common.dismiss} onClick={() => setUploads((x) => x.filter((j) => j.id !== u.id))}><X className="ico" size={14} aria-hidden /></button>
                </>
              ) : (
                <>
                  <span className="dim tiny">
                    {u.progress.stage === 'hashing' ? t.videos.hashing : `${megabytes(u.progress.done)} / ${megabytes(u.progress.total)}`}
                  </span>
                  <span className="job-phase-bar" role="progressbar" aria-label={u.name}>
                    <span
                      className={`job-phase-fill${u.progress.stage === 'hashing' ? ' indeterminate' : ''}`}
                      style={{ width: `${u.progress.total ? Math.round((u.progress.done / u.progress.total) * 100) : 0}%` }}
                    />
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {loaded && items.length === 0 && <Empty>{t.sources.none[kind]}</Empty>}
      {/* Rows, not a table: a file is a name, a size and a way to remove it; its state is said only
          when it is not simply ready. */}
      {items.length > 0 && (
        <ul className="source-rows">
          {items.map((s) => (
            <li key={s.source_id} className="source-row">
              <span className="source-name" title={`${s.filename} · ${s.sha256.slice(0, 12)}`}>{s.filename}</span>
              {s.status !== 'ready' && (
                <Badge kind={s.status === 'error' ? 'bad' : 'warn'} title={s.error ?? undefined}>
                  {t.videos.status[s.status]}
                </Badge>
              )}
              <span className="dim tiny source-size">{megabytes(s.size)}</span>
              <button className="ghost icon tiny danger-hover" aria-label={t.sources.delete}
                      disabled={!auth.mayTouch(s.uploaded_by)}
                      title={auth.mayTouch(s.uploaded_by) ? t.sources.delete : t.common.notYours}
                      onClick={() => onDelete(s)}>
                <Trash2 className="ico" size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
